import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';

const QUESTIONS = [
  {
    id: 'role',
    question: 'What best describes your real estate investing style?',
    options: [
      { value: 'wholesaler',   label: '🏷️ Wholesaler',         desc: 'I assign contracts to other investors' },
      { value: 'flipper',      label: '🔨 House Flipper',       desc: 'I buy, rehab, and sell for profit' },
      { value: 'brrrr',        label: '🔄 BRRRR Investor',      desc: 'Buy, Rehab, Rent, Refinance, Repeat' },
      { value: 'buy_hold',     label: '🏠 Buy & Hold',          desc: 'I rent properties for passive income' },
      { value: 'new',          label: '🌱 Just Getting Started', desc: 'I\'m learning the ropes' },
    ],
  },
  {
    id: 'volume',
    question: 'How many deals do you analyze or close per month?',
    options: [
      { value: '1_5',    label: '1 – 5 deals',    desc: 'Part-time or just starting out' },
      { value: '6_20',   label: '6 – 20 deals',   desc: 'Active investor' },
      { value: '21_50',  label: '21 – 50 deals',  desc: 'High-volume operator' },
      { value: '50plus', label: '50+ deals',       desc: 'Full-scale business' },
    ],
  },
  {
    id: 'market',
    question: 'What markets do you primarily invest in?',
    options: [
      { value: 'local',    label: '📍 Local (1 market)',         desc: 'I know my city inside and out' },
      { value: 'regional', label: '🗺️ Regional (2–5 markets)',   desc: 'I invest across nearby states' },
      { value: 'national', label: '🌎 National (6+ markets)',    desc: 'I invest anywhere the numbers work' },
      { value: 'virtual',  label: '💻 Virtual / Remote',         desc: 'I invest out-of-state remotely' },
    ],
  },
  {
    id: 'price_range',
    question: "What's your typical target purchase price?",
    options: [
      { value: 'under_100k',    label: 'Under $100K',       desc: 'Budget-friendly markets' },
      { value: '100k_250k',     label: '$100K – $250K',     desc: 'Mid-tier properties' },
      { value: '250k_500k',     label: '$250K – $500K',     desc: 'Higher-end markets' },
      { value: '500k_plus',     label: '$500K+',            desc: 'Luxury or coastal markets' },
    ],
  },
  {
    id: 'challenge',
    question: "What's your biggest challenge when evaluating deals?",
    options: [
      { value: 'arv',      label: '📊 Getting an accurate ARV',   desc: 'Hard to know what a property is really worth' },
      { value: 'rehab',    label: '🔧 Estimating rehab costs',    desc: 'Scope creep kills my margins' },
      { value: 'speed',    label: '⚡ Analyzing deals fast enough', desc: 'Deals go before I can run numbers' },
      { value: 'finding',  label: '🔍 Finding deals at all',      desc: 'The pipeline is the problem' },
    ],
  },
];

export default function Onboarding() {
  useAuth();
  const navigate  = useNavigate();
  const [searchParams] = useSearchParams();
  const tier   = searchParams.get('tier')   || 'starter';
  const annual = searchParams.get('annual') === 'true';

  const [step, setStep]       = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const current  = QUESTIONS[step];
  const total    = QUESTIONS.length;
  const selected = answers[current.id];

  async function select(value) {
    const updated = { ...answers, [current.id]: value };
    setAnswers(updated);

    if (step < total - 1) {
      setTimeout(() => setStep(s => s + 1), 220); // brief pause so selection is visible
    } else {
      // Last question — go to Stripe
      setTimeout(() => startCheckout(), 220);
    }
  }

  async function startCheckout() {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('proparv_token');
    if (!token) { navigate('/login?next=/pricing'); return; }

    try {
      const res  = await fetch('/api/stripe/checkout', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ tier, annual }),
      });
      const data = await res.json();
      if (res.status === 401) { navigate('/login?next=/pricing'); return; }
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.message || data.error || 'Could not start checkout. Please try again.');
        setLoading(false);
      }
    } catch {
      setError('Cannot reach server. Make sure the backend is running.');
      setLoading(false);
    }
  }

  const planColors = { starter: '#3b82f6', pro: '#8b5cf6', unlimited: '#f59e0b' };
  const planColor  = planColors[tier] || '#3b82f6';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px 40px', background: 'var(--bg-dark)' }}>
      <div style={{ width: '100%', maxWidth: 580 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: `${planColor}18`, border: `1px solid ${planColor}44`, borderRadius: 20, padding: '5px 14px', marginBottom: 20 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: planColor }} />
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: planColor, textTransform: 'capitalize' }}>
              {tier} Plan · {annual ? 'Annual' : 'Monthly'}
            </span>
          </div>
          <h1 style={{ fontSize: '1.7rem', fontWeight: 900, color: 'var(--text-primary)', marginBottom: 8, letterSpacing: '-0.02em' }}>
            Quick setup — 5 questions
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
            Help us tailor PROPARV to how you invest
          </p>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 8 }}>
            <span>Question {step + 1} of {total}</span>
            <span>{Math.round(((step + 1) / total) * 100)}% complete</span>
          </div>
          <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${((step + 1) / total) * 100}%`,
              background: `linear-gradient(90deg, ${planColor}, ${planColor}cc)`,
              borderRadius: 3,
              transition: 'width 0.4s ease'
            }} />
          </div>
        </div>

        {/* Question card */}
        <div className="card" style={{ padding: '32px 28px', marginBottom: 20 }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 24, lineHeight: 1.4 }}>
            {current.question}
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {current.options.map(opt => {
              const isSelected = selected === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => select(opt.value)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '14px 18px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                    border: `2px solid ${isSelected ? planColor : 'var(--border)'}`,
                    background: isSelected ? `${planColor}14` : 'var(--bg-card)',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${isSelected ? planColor : 'var(--border)'}`,
                    background: isSelected ? planColor : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}>
                    {isSelected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: isSelected ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                      {opt.label}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>
                      {opt.desc}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fca5a5', fontSize: '0.9rem', marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {step > 0 ? (
            <button
              onClick={() => setStep(s => s - 1)}
              disabled={loading}
              style={{
                padding: '10px 20px', borderRadius: 10, border: '1px solid var(--border)',
                background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600,
                cursor: 'pointer', fontSize: '0.9rem'
              }}
            >
              ← Back
            </button>
          ) : <div />}

          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              <div className="spinner" style={{ width: 16, height: 16 }} />
              Redirecting to checkout…
            </div>
          )}

          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {step + 1} / {total}
          </div>
        </div>

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          Your answers help us improve the product — we never sell your data.
        </p>
      </div>
    </div>
  );
}
